import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useState } from "react"
import type { ComponentProps } from "react"
import { cn } from "cn"

function clamp(value: number, min?: number, max?: number) {
  let result = value
  if (min !== undefined) {
    result = Math.max(min, result)
  }
  if (max !== undefined) {
    result = Math.min(max, result)
  }
  return result
}

export type InputCounterProps = Omit<
  ComponentProps<"input">,
  "value" | "onChange" | "type"
> & {
  value?: number
  onChange?: (value: number) => void
  min?: number
  max?: number
  step?: number
}

/**
 * Figma-Component-Set „Input Counter" (node 9420:51533). Die Zahl ist wie im
 * Figma-State „focus" (Textcursor sichtbar) zusätzlich zu den +/- Buttons
 * per Tastatur direkt editierbar (Nutzer-Entscheidung).
 */
function InputCounter({
  className,
  value = 0,
  onChange,
  min,
  max,
  step = 1,
  disabled,
  id,
  "aria-invalid": ariaInvalid,
  ...props
}: InputCounterProps) {
  const [text, setText] = useState(() => String(value))
  const [prevValue, setPrevValue] = useState(value)

  // Extern geänderter Wert synchron ins Textfeld übernehmen — bewusst während des Renders
  // (React „Adjusting state when a prop changes"-Pattern), nicht in einem Effect.
  if (value !== prevValue) {
    setPrevValue(value)
    setText(String(value))
  }

  function commit(next: number) {
    const clamped = clamp(next, min, max)
    setText(String(clamped))
    onChange?.(clamped)
  }

  const decrementDisabled = disabled || (min !== undefined && value <= min)
  const incrementDisabled = disabled || (max !== undefined && value >= max)

  return (
    <div
      data-slot="input-counter"
      className={cn(
        "flex h-9 w-full items-center justify-between gap-2 rounded-full border-2 border-neutrals-v2-200 bg-backgrounds-primary px-4 py-2 text-sm leading-5 transition-colors has-[[data-slot=input-counter-field]:disabled]:cursor-not-allowed has-[[data-slot=input-counter-field]:focus]:border-brand-blue-100 has-[[data-slot=input-counter-field][aria-invalid=true]]:border-brand-red-200",
        className
      )}
    >
      <button
        type="button"
        disabled={decrementDisabled}
        aria-label="Verringern"
        data-slot="input-counter-decrement"
        onClick={() => commit(value - step)}
        className="shrink-0 text-labels-primary outline-none disabled:cursor-not-allowed disabled:text-labels-tertiary"
      >
        <FontAwesomeIcon icon={faMinus} className="size-[18px]" />
      </button>
      <input
        id={id}
        data-slot="input-counter-field"
        type="text"
        inputMode="numeric"
        disabled={disabled}
        aria-invalid={ariaInvalid}
        value={text}
        onChange={(event) => {
          const raw = event.target.value
          if (raw === "" || /^-?\d*$/.test(raw)) {
            setText(raw)
          }
        }}
        onBlur={() => commit(text === "" || text === "-" ? 0 : Number(text))}
        className="w-7 shrink-0 bg-transparent text-center text-labels-primary outline-none disabled:cursor-not-allowed disabled:text-labels-tertiary"
        {...props}
      />
      <button
        type="button"
        disabled={incrementDisabled}
        aria-label="Erhöhen"
        data-slot="input-counter-increment"
        onClick={() => commit(value + step)}
        className="shrink-0 text-labels-primary outline-none disabled:cursor-not-allowed disabled:text-labels-tertiary"
      >
        <FontAwesomeIcon icon={faPlus} className="size-[18px]" />
      </button>
    </div>
  )
}
InputCounter.displayName = "InputCounter"

export { InputCounter }
